import { FC, useCallback, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { Form, handleServerErrors, PracticeTest } from 'pagesNew/PracticeTests/Form'
import { useLink } from 'routes/cmsRoutes'
import { DELETE_PRACTICE_TEST, GET_ONE_PRACTICE_TEST, UPDATE_PRACTICE_TEST } from 'queries/practiceTest'
import { CreatePracticeTestInput, PracticeTestTypeEnum } from '__generatedTypes__/globalTypes'
import { FIND_SCHOOL_LIST_BY_PLATFORM } from 'queries/faculty'
import {
  findSchoolListByPlatform,
  findSchoolListByPlatformVariables,
} from '__generatedTypes__/findSchoolListByPlatform'
import { useHistory } from 'hooks/useHistory'
import { PageLoader } from 'components/PageLoader'
import { updatePracticeTest, updatePracticeTestVariables } from '__generatedTypes__/updatePracticeTest'
import { findOnePracticeTest, findOnePracticeTestVariables } from '__generatedTypes__/findOnePracticeTest'
import { removePracticeTest, removePracticeTestVariables } from '__generatedTypes__/removePracticeTest'
import { toast } from 'utils/toast'
import { PlatformSlug } from 'constants/consts'
import { CourseName } from 'theme/utils'
import { School } from 'pagesNew/Users/Edit/components/hooks'
import { COMMON_PTS_CONFIG } from '../consts'

export const Edit: FC<{ type?: PracticeTestTypeEnum }> = ({ type: typeProp = PracticeTestTypeEnum.PracticeTest }) => {
  const { practiceTestId = '' } = useParams()
  const { navigate } = useLink()
  const { loadingHistory, populateHistoryWithUser } = useHistory()
  const [update] = useMutation<updatePracticeTest, updatePracticeTestVariables>(UPDATE_PRACTICE_TEST)
  const [remove] = useMutation<removePracticeTest, removePracticeTestVariables>(DELETE_PRACTICE_TEST)
  const { data, loading } = useQuery<findOnePracticeTest, findOnePracticeTestVariables>(GET_ONE_PRACTICE_TEST, {
    variables: {
      params: practiceTestId,
    },
    fetchPolicy: 'network-only',
  })
  const practiceTest = data?.findOnePracticeTest
  const questions = data?.findQuestionByRelation ?? []
  const type: PracticeTestTypeEnum = practiceTest?.type ?? typeProp
  const config = useMemo(() => COMMON_PTS_CONFIG[type], [type])
  const subject = practiceTest?.subject
  const title = config?.titleSingle ?? ''
  const dashboardLink = config?.dashboardLink ?? ''

  const isInPersonTest = [PracticeTestTypeEnum.InPersonMockTest].includes(type)
  const schoolsState = useQuery<findSchoolListByPlatform, findSchoolListByPlatformVariables>(
    FIND_SCHOOL_LIST_BY_PLATFORM,
    {
      variables: {
        platformId: String(subject?.platform.id),
      },
      skip: !isInPersonTest || !subject?.platform.id,
      fetchPolicy: 'cache-and-network',
    }
  )
  const schools = useMemo(
    () =>
      schoolsState.data?.findSchoolListByPlatform?.schoolArray?.map(
        (school) =>
          ({
            id: school.key,
            title: school.value,
          } as School)
      ),
    [schoolsState.data?.findSchoolListByPlatform]
  )

  const loadingSchools = isInPersonTest && schoolsState.loading

  const handleSubmit = useCallback(
    async (practiceTest: CreatePracticeTestInput) => {
      try {
        const { data } = await update({
          variables: {
            params: {
              id: practiceTestId,
              ...practiceTest,
              history: populateHistoryWithUser(practiceTest.history),
            },
          },
        })

        if (data?.updatePracticeTest.id) {
          navigate(dashboardLink)
          toast.success(`${title} updated successfully`)
        }
      } catch (error) {
        toast.error(`Failed to create ${title}`)
        return handleServerErrors(error, title)
      }
    },
    [type, populateHistoryWithUser, practiceTestId, update, navigate, subject]
  )

  const handleDelete = useCallback(async () => {
    try {
      const { data } = await remove({
        variables: {
          params: practiceTestId,
        },
      })

      if (data?.removePracticeTest) {
        navigate(dashboardLink)
        toast.success(`${title} deleted successfully`)
      } else {
        toast.error(`Failed to delete ${title}`)
      }
    } catch (error) {
      toast.error(`Failed to delete ${title}`)
    }
  }, [type, practiceTestId, update, navigate, subject])

  useEffect(() => {
    if (!loading && (!practiceTest || !subject)) {
      navigate(dashboardLink)
      toast.error(`Subject not found, redirecting to ${title} list`)
    }
  }, [type, loading, practiceTest, subject])

  if (loading || !practiceTest || !subject || loadingHistory || loadingSchools) {
    return <PageLoader />
  }

  return (
    <Form
      type={type}
      title={title}
      practiceTestOrSubjectId={
        {
          ...practiceTest,
          questions,
        } as PracticeTest
      }
      subject={{
        ...subject,
        tabName:
          subject.platform.slug === PlatformSlug.CARSBooster
            ? CourseName.ReadingComprehension
            : CourseName.PerceptualAbility,
      }}
      passFailStandardizedScore={[PracticeTestTypeEnum.MockTest, PracticeTestTypeEnum.InPersonMockTest].includes(type)}
      isInPersonTest={isInPersonTest}
      schools={schools ?? []}
      tagLabel={subject.platform.slug.toUpperCase()}
      onSubmit={handleSubmit}
      onDelete={handleDelete}
      getDeleteDisabledReason={(isLive) => (isLive ? `You can't delete ${title} because Live is enabled` : undefined)}
    />
  )
}
